import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { getMovieById, getRelatedMovies, categories } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-background-800 rounded-full mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-200 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该影片可能已下架或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium rounded-lg whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie).slice(0, 5);
  const cat = categories.find((c) => c.slug === movie.category);
  const catName = cat ? cat.name : movie.category;

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: catName, href: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />

          {/* Detail Hero */}
          <section className="w-full px-4 md:px-6 py-6 md:py-10">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster */}
              <div className="flex-shrink-0 w-full sm:w-[280px] mx-auto lg:mx-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-800">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={`${movie.title} - 91电影视频免费在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-3">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary-500/15 text-primary-400 text-sm font-semibold">
                    <i className="ri-star-fill"></i>
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  {movie.isHot && (
                    <span className="px-2 py-0.5 rounded bg-accent-500/15 text-accent-400 text-xs font-medium">
                      热门
                    </span>
                  )}
                  {movie.isNew && (
                    <span className="px-2 py-0.5 rounded bg-accent-500/15 text-accent-400 text-xs font-medium">
                      最新
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-background-800 text-foreground-400 border border-background-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Play button */}
                <div className="flex items-center gap-4 mb-6">
                  <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-lg text-sm whitespace-nowrap cursor-pointer transition-colors">
                    <i className="ri-play-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-800 hover:bg-background-700 text-foreground-400 hover:text-accent-400 cursor-pointer transition-colors">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-800 hover:bg-background-700 text-foreground-400 hover:text-primary-400 cursor-pointer transition-colors">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>

                {/* Meta info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 min-w-[60px] whitespace-nowrap">导演：</span>
                    <span className="text-sm text-foreground-300">
                      <Link to="#" className="hover:text-primary-400 transition-colors">
                        {movie.director}
                      </Link>
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 min-w-[60px] whitespace-nowrap">主演：</span>
                    <div className="flex flex-wrap gap-1.5">
                      {movie.actors.map((actor) => (
                        <Link
                          key={actor}
                          to="#"
                          className="text-sm text-foreground-300 hover:text-primary-400 transition-colors"
                        >
                          {actor}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 min-w-[60px] whitespace-nowrap">类型：</span>
                    <span className="text-sm text-foreground-300">{movie.subcategory}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 min-w-[60px] whitespace-nowrap">更新：</span>
                    <span className="text-sm text-accent-400">{movie.updateInfo}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 min-w-[60px] whitespace-nowrap">播放量：</span>
                    <span className="text-sm text-foreground-300">
                      {(movie.views / 10000).toFixed(0)}万次
                    </span>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-background-800/50 rounded-xl p-5 border border-background-700/30">
                  <h3 className="text-sm font-heading font-semibold text-foreground-300 mb-3">
                    剧情简介
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {movie.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Related */}
          {relatedMovies.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-900/50">
              <div className="mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  相关推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">你可能还喜欢这些</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} variant="default" />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.image,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.actors.map((a) => ({
              '@type': 'Person',
              name: a,
            })),
            genre: movie.tags,
            countryOfOrigin: {
              '@type': 'Country',
              name: movie.region,
            },
          }),
        }}
      />
    </>
  );
}